import type { ChatMention } from '../types';

/** A span is live only while the text under it still reads exactly `@Name`. */
export function spanMatches(text: string, mention: ChatMention): boolean {
  return text.slice(mention.start, mention.start + mention.length) === `@${mention.name}`;
}

/**
 * Keep mention offsets true after an arbitrary edit to the composer.
 *
 * The composer is a plain text input, so the user can backspace into the middle of a
 * chip, paste over half the box, or type anywhere. Rather than trying to guess what they
 * meant, this diffs old against new by common prefix/suffix and applies one blunt rule:
 *
 *   - a mention entirely before the changed region  -> untouched
 *   - a mention entirely after it                   -> shifted by the length delta
 *   - a mention overlapping it                      -> DROPPED
 *
 * Dropping is the correct answer, not a shortcut. A half-deleted `@Sofia Cut` must not
 * still notify Sofia, and the chip vanishing on the first backspace is what every other
 * app that does this trains people to expect. The final spanMatches pass is the belt to
 * that braces: anything whose text no longer reads `@Name` goes, whatever the arithmetic
 * said.
 */
export function reconcileMentions(
  prevText: string,
  mentions: ChatMention[],
  nextText: string,
): ChatMention[] {
  if (mentions.length === 0) return mentions;
  if (prevText === nextText) return mentions;

  let prefix = 0;
  while (prefix < prevText.length && prefix < nextText.length && prevText[prefix] === nextText[prefix]) {
    prefix += 1;
  }

  let suffix = 0;
  while (
    suffix < prevText.length - prefix &&
    suffix < nextText.length - prefix &&
    prevText[prevText.length - 1 - suffix] === nextText[nextText.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  // The edit replaced prevText[prefix .. prevText.length - suffix).
  const changedStart = prefix;
  const changedEnd = prevText.length - suffix;
  const delta = nextText.length - prevText.length;

  return mentions
    .reduce<ChatMention[]>((kept, mention) => {
      const end = mention.start + mention.length;
      if (end <= changedStart) kept.push(mention);
      else if (mention.start >= changedEnd) kept.push({ ...mention, start: mention.start + delta });
      return kept;
    }, [])
    .filter((mention) => spanMatches(nextText, mention));
}

/**
 * Re-base mentions onto the trimmed text that actually gets inserted.
 *
 * handleSubmit sends `newMessage.trim()`, so leading whitespace shifts every offset left.
 * Getting this wrong renders as a chip sitting one character off -- which looks like a
 * font bug, not an offset bug, and would sail through review. So it is a tested function
 * rather than an inline subtraction, and it re-checks the span invariant before returning.
 */
export function trimMentions(
  text: string,
  mentions: ChatMention[],
): { text: string; mentions: ChatMention[] } {
  const trimmed = text.trim();
  if (mentions.length === 0) return { text: trimmed, mentions: [] };

  const offset = text.length - text.trimStart().length;
  const rebased = mentions
    .map((mention) => ({ ...mention, start: mention.start - offset }))
    .filter((mention) => mention.start >= 0 && spanMatches(trimmed, mention));

  return { text: trimmed, mentions: rebased };
}
